<template>
    <login-register-box>
        <template v-slot:formBox>
            <div class="register-title">
                <div class="register-title-en">SIGN-UP</div>
                <div class="register-title-cn">注册</div>
            </div>
            <div class="register-login-nav">
                <span>已有账号？</span>
                <router-link to="/login">立即登录！</router-link>
            </div>
            <div class="register-form-box">
                <el-form
                    label-position="top"
                    :model="form"
                    :rules="rules"
                    ref="registerForm">
                    <el-form-item label="昵称 / Nickname" prop="nickname">
                        <el-input v-model.trim="form.nickname" maxlength="30" />
                    </el-form-item>
                    <el-form-item label="手机号 / Phone Number" prop="phone">
                        <el-input v-model.trim="form.phone" autocomplete="tel" />
                    </el-form-item>
                    <el-form-item label="短信验证码 / Verification Code" prop="smsCode">
                        <el-input v-model.trim="form.smsCode" maxlength="6" autocomplete="one-time-code">
                            <el-button
                                slot="suffix"
                                type="info"
                                size="mini"
                                @click="handleSendCode"
                                :disabled="codeCountdown > 0"
                                plain>
                                {{ codeCountdown > 0 ? `${codeCountdown}s 后重试` : '获取验证码' }}
                            </el-button>
                        </el-input>
                    </el-form-item>
                    <el-form-item label="密码 / Password" prop="password">
                        <el-input
                            v-model="form.password"
                            type="password"
                            maxlength="64"
                            autocomplete="new-password"
                            @keyup.enter.native="handleRegister" />
                    </el-form-item>
                    <el-form-item label="邀请码 / Invitation Code" prop="inviteCode">
                        <el-input
                            v-model.trim="form.inviteCode"
                            maxlength="8"
                            show-word-limit />
                    </el-form-item>
                    <el-form-item prop="accepted">
                        <el-checkbox v-model="form.accepted">
                            我已阅读并同意《用户协议》和《隐私政策》
                        </el-checkbox>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="warning" class="register-form-submit" @click="handleRegister">
                            注册
                        </el-button>
                    </el-form-item>
                </el-form>
            </div>
        </template>
    </login-register-box>
</template>

<script>
import { mapActions } from "vuex";
import { isValidPassword, PASSWORD_VALIDATION_MESSAGE } from "@/tool/password";

const validatePhone = (rule, value, callback) => {
    if (!/^1[3-9]\d{9}$/.test(value || "")) {
        callback(new Error("请输入正确的中国大陆手机号"));
        return;
    }
    callback();
};

const validateSmsCode = (rule, value, callback) => {
    if (!/^\d{6}$/.test(value || "")) {
        callback(new Error("请输入短信验证码"));
        return;
    }
    callback();
};

const INVITE_CODE_CHARSET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

const validateInviteCode = (rule, value, callback) => {
    const inviteCode = (value || "").trim();
    if (!inviteCode) {
        callback();
        return;
    }
    const pattern = new RegExp(`^[${INVITE_CODE_CHARSET}]{8}$`, "i");
    if (!pattern.test(inviteCode)) {
        callback(new Error("请输入 8 位有效邀请码"));
        return;
    }
    callback();
};

const validateAccepted = (rule, value, callback) => {
    if (!value) {
        callback(new Error("请先同意用户协议和隐私政策"));
        return;
    }
    callback();
};

export default {
    data() {
        const validatePassword = (rule, value, callback) => {
            if (!isValidPassword(value || "")) {
                callback(new Error(PASSWORD_VALIDATION_MESSAGE));
                return;
            }
            callback();
        };

        return {
            form: {
                nickname: "",
                inviteCode: "",
                phone: "",
                smsCode: "",
                password: "",
                accepted: false,
            },
            rules: {
                phone: [{ required: true, validator: validatePhone, trigger: "blur" }],
                smsCode: [{ required: true, validator: validateSmsCode, trigger: "blur" }],
                password: [{ required: true, validator: validatePassword, trigger: "blur" }],
                inviteCode: [{ validator: validateInviteCode, trigger: "blur" }],
                accepted: [{ required: true, validator: validateAccepted, trigger: "change" }],
            },
            codeCountdown: 0,
            codeTimer: null,
        };
    },
    components: {
        LoginRegisterBox: () => import("@/components/LoginRegisterBox"),
    },
    methods: {
        ...mapActions(["sendCode", "postRegister"]),
        startCountdown() {
            this.codeCountdown = 60;
            clearInterval(this.codeTimer);
            this.codeTimer = setInterval(() => {
                this.codeCountdown -= 1;
                if (this.codeCountdown <= 0) {
                    clearInterval(this.codeTimer);
                    this.codeTimer = null;
                }
            }, 1000);
        },
        handleSendCode() {
            this.$refs.registerForm.validateField("phone", (errorMessage) => {
                if (errorMessage) return;
                this.sendCode(this.form.phone).then(({ message, smsCode }) => {
                    this.$message.success(message);
                    if (smsCode) {
                        this.form.smsCode = smsCode;
                    }
                    this.startCountdown();
                }).catch((message) => this.$message.error(message));
            });
        },
        handleRegister() {
            this.$refs.registerForm.validate((valid) => {
                if (!valid) return;
                this.postRegister({
                    nickname: this.form.nickname,
                    inviteCode: this.form.inviteCode,
                    phone: this.form.phone,
                    password: this.form.password,
                    smsCode: this.form.smsCode,
                    agreementAccepted: this.form.accepted,
                    privacyAccepted: this.form.accepted,
                }).then((result) => {
                    this.$message({ message: result.msg, type: result.type });
                    if (result.type === "success") {
                        setTimeout(() => this.$router.push("/login"), 500);
                    }
                });
            });
        },
    },
    beforeDestroy() {
        clearInterval(this.codeTimer);
    },
};
</script>

<style scoped>
.register-title > div {
    font-size: 30px;
    text-align: center;
}

.register-title-en {
    font-weight: bold;
}

.register-login-nav {
    margin: 30px 0 10px;
}

.register-form-box {
    margin-bottom: 10px;
    padding: 20px;
    border: 1px solid #EBEEF5;
    border-radius: 10px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
}

.register-form-box >>> .el-form-item__label {
    margin: 0;
    padding: 0;
    color: #909399;
}

.register-form-submit {
    width: 100%;
}
</style>
